import { createSlice } from "@reduxjs/toolkit";
import { asyncStatus } from "../../utils/asyncStatus";
import {
  error_toast_message,
  success_toast_message,
} from "../../utils/toast_message";
import {
  getAdminTickets,
  getAdminTicketById,
  adminReplyToTicket,
  updateTicketStatus,
  assignTicket,
} from "../../services/SupportTicketService";

const initialState = {
  // All tickets
  tickets_status: asyncStatus.IDLE,
  tickets: [],
  tickets_stats: null,
  tickets_error: null,

  // Single ticket
  current_ticket_status: asyncStatus.IDLE,
  current_ticket: null,
  current_ticket_error: null,

  // Reply
  reply_status: asyncStatus.IDLE,
  reply_error: null,

  // Update status
  update_status_status: asyncStatus.IDLE,
  update_status_error: null,

  // Assign
  assign_status: asyncStatus.IDLE,
  assign_error: null,
};

const supportTicketsSlice = createSlice({
  name: "supportTicketsSlice",
  initialState,
  reducers: {
    resetReplyStatus: (state) => {
      state.reply_status = asyncStatus.IDLE;
      state.reply_error = null;
    },
    clearCurrentTicket: (state) => {
      state.current_ticket = null;
      state.current_ticket_status = asyncStatus.IDLE;
    },
    setCurrentTicket: (state, action) => {
      state.current_ticket = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get All Tickets
    builder.addCase(getAdminTickets.pending, (state) => {
      state.tickets_status = asyncStatus.LOADING;
    });
    builder.addCase(getAdminTickets.fulfilled, (state, { payload }) => {
      state.tickets_status = asyncStatus.SUCCEEDED;
      state.tickets = payload.tickets || [];
      state.tickets_stats = payload.stats || null;
    });
    builder.addCase(getAdminTickets.rejected, (state, action) => {
      state.tickets_status = asyncStatus.ERROR;
      state.tickets_error = action.payload;
      error_toast_message(action.payload);
    });

    // Get Ticket By ID
    builder.addCase(getAdminTicketById.pending, (state) => {
      state.current_ticket_status = asyncStatus.LOADING;
    });
    builder.addCase(getAdminTicketById.fulfilled, (state, { payload }) => {
      state.current_ticket_status = asyncStatus.SUCCEEDED;
      state.current_ticket = payload.ticket;
    });
    builder.addCase(getAdminTicketById.rejected, (state, action) => {
      state.current_ticket_status = asyncStatus.ERROR;
      state.current_ticket_error = action.payload;
      error_toast_message(action.payload);
    });

    // Reply to Ticket
    builder.addCase(adminReplyToTicket.pending, (state) => {
      state.reply_status = asyncStatus.LOADING;
    });
    builder.addCase(adminReplyToTicket.fulfilled, (state, { payload }) => {
      state.reply_status = asyncStatus.SUCCEEDED;
      if (state.current_ticket) {
        state.current_ticket.replies = [
          ...(state.current_ticket.replies || []),
          payload.reply,
        ];
        state.current_ticket.status = payload.ticket?.status || state.current_ticket.status;
      }
      // Update in the list as well
      const index = state.tickets.findIndex(
        (t) => t.id === state.current_ticket?.id
      );
      if (index !== -1) {
        state.tickets[index].status = payload.ticket?.status || state.tickets[index].status;
        state.tickets[index].replies = state.current_ticket.replies;
      }
      success_toast_message("Reply sent successfully");
    });
    builder.addCase(adminReplyToTicket.rejected, (state, action) => {
      state.reply_status = asyncStatus.ERROR;
      state.reply_error = action.payload;
      error_toast_message(action.payload);
    });

    // Update Ticket Status
    builder.addCase(updateTicketStatus.pending, (state) => {
      state.update_status_status = asyncStatus.LOADING;
    });
    builder.addCase(updateTicketStatus.fulfilled, (state, { payload }) => {
      state.update_status_status = asyncStatus.SUCCEEDED;
      if (state.current_ticket) {
        state.current_ticket.status = payload.ticket.status;
        state.current_ticket.resolved_at = payload.ticket.resolved_at;
      }
      // Update in the list as well
      const index = state.tickets.findIndex(
        (t) => t.id === payload.ticket.id
      );
      if (index !== -1) {
        state.tickets[index] = {
          ...state.tickets[index],
          ...payload.ticket,
        };
      }
      success_toast_message(payload.message || "Status updated successfully");
    });
    builder.addCase(updateTicketStatus.rejected, (state, action) => {
      state.update_status_status = asyncStatus.ERROR;
      state.update_status_error = action.payload;
      error_toast_message(action.payload);
    });

    // Assign Ticket
    builder.addCase(assignTicket.pending, (state) => {
      state.assign_status = asyncStatus.LOADING;
    });
    builder.addCase(assignTicket.fulfilled, (state, { payload }) => {
      state.assign_status = asyncStatus.SUCCEEDED;
      if (state.current_ticket) {
        state.current_ticket.assigned_to = payload.ticket.assigned_to;
      }
      success_toast_message(payload.message || "Ticket assigned successfully");
    });
    builder.addCase(assignTicket.rejected, (state, action) => {
      state.assign_status = asyncStatus.ERROR;
      state.assign_error = action.payload;
      error_toast_message(action.payload);
    });
  },
});

export const {
  resetReplyStatus,
  clearCurrentTicket,
  setCurrentTicket,
} = supportTicketsSlice.actions;

export default supportTicketsSlice.reducer;
